import { FILE_EXTENSION } from '@/config'
import { Camera, Project, Store } from '@/model/types'
import * as v1Schema from '@/schema/v1'
import { checkPresent } from '@/ts_utils'
import * as solver from '@/utils/solver'
import { Component, Ref, Vue } from 'vue-property-decorator'
import ProjectEditor from './ProjectEditor.vue'

function makeDefaultCamera (width: number, height: number): Camera {
  return {
    xOff: 0,
    yOff: 0,
    zoom: 1,
    width: width,
    height: height
  }
}

function makeBlankProject (): Project {
  return {
    dependencies: {},
    tasks: {
      1: {
        x: 0,
        y: 0,
        title: 'New Task',
        duration: 1,
        createdAt: new Date(),
        completedAt: null,
        deletedAt: null,
        estimatedCompletion: null,
        priority: null
      }
    }
  }
}

@Component({
  components: {
    ProjectEditor
  }
})
export default class App extends Vue {
  @Ref('inputFile') readonly inputFile!: HTMLInputElement;

  private showSidebar = false;
  private store: Store = {
    camera: makeDefaultCamera(100, 100),
    project: makeBlankProject()
  };

  private mounted () {
    const autoSave = () => {
      window.localStorage.autosave = v1Schema.saveProject(this.store.project)
    }

    function scheduledAutoSave () {
      autoSave()
      setTimeout(scheduledAutoSave, 30000)
    }

    setTimeout(scheduledAutoSave, 30000)

    // Set up final autosave
    window.addEventListener('beforeunload', autoSave)

    // Load last autosave
    const saved = window.localStorage.autosave
    if (saved) {
      const project = v1Schema.loadProject(saved)
      solver.solve(project)
      this.store.project = project
    }
  }

  private triggerFileChooser () {
    this.inputFile.click()
  }

  private async handleFileChosen () {
    const files = this.inputFile.files
    if (files === null || files.length === 0) return
    const file = files.item(0)
    checkPresent(file)
    const contents = await file.text()
    const project = v1Schema.loadProject(contents)
    solver.solve(project)
    this.store = {
      camera: makeDefaultCamera(this.store.camera.width, this.store.camera.height),
      project
    }

    this.showSidebar = false
  }

  private handleSave () {
    const text = v1Schema.saveProject(this.store.project)

    // https://ourcodeworld.com/articles/read/189/how-to-create-a-file-and-generate-a-download-with-javascript-in-the-browser-without-a-server
    const element = document.createElement('a')
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text))
    element.setAttribute('download', `project${FILE_EXTENSION}`)

    element.style.display = 'none'
    document.body.appendChild(element)

    element.click()

    document.body.removeChild(element)

    this.showSidebar = false
  }

  private createNew () {
    this.store = {
      camera: makeDefaultCamera(this.store.camera.width, this.store.camera.height),
      project: makeBlankProject()
    }
    this.showSidebar = false
  }

  get fileExtension () {
    return FILE_EXTENSION
  }
}
